// Third-party packages
import {
	getOidcAccessToken,
} from '@helpers/oidcToken';

// Internal libraries (@stonal-tech/*)
import {
	buildUrl,
	getOrganizationDataSource as resolveDataSource,
	type OrganizationDataSource,
} from '@stonal-tech/lib-front-utils-ts';

// Project modules
import {
	logger,
} from '@config/logger';
import {
	AppDescription,
} from '@local_types/index';

// Relative imports
import {
	STORAGE_KEY_ORG_DATA_SOURCE,
} from '../../config/storageKeys';
import {
	URL_PATTERNS,
} from '../../config/urlPatterns';

type CachedDataSource = {
	[organization: string]: OrganizationDataSource;
};

/**
 * Reads the data source cached for an organization on a previous page load.
 *
 * Keyed per organization rather than holding a single one: a user who moves between
 * two organizations would otherwise evict each on arriving at the other, so every
 * switch would miss the cache and re-resolve. Since resolution is asynchronous while
 * the page event that follows a switch is not, that miss costs the `org_data_source`
 * property on that event — not just a redundant request.
 *
 * A payload written by an older version had a different shape and simply misses here,
 * which re-resolves once and rewrites it.
 *
 * @param organization - Organization code to read
 * @returns The cached data source, or undefined when this organization has none
 */
export const readCachedOrganizationDataSource = (organization: string | undefined): OrganizationDataSource | undefined => {
	if (!organization) {
		return undefined;
	}

	try {
		const cached = localStorage.getItem(STORAGE_KEY_ORG_DATA_SOURCE);
		if (!cached) {
			return undefined;
		}

		const parsed = JSON.parse(cached) as CachedDataSource;
		const value = parsed?.[organization];

		// Only our own writes land here, so a string is enough of a shape check;
		// anything else (an older payload, a corrupted entry) simply misses.
		return typeof value === 'string' ? value : undefined;
	} catch {
		return undefined;
	}
};

/**
 * Persists the data source resolved for an organization, alongside the ones already
 * known, so that returning to any of them reads the cache instead of re-resolving.
 *
 * @param organization - Organization code the value belongs to
 * @param value - Resolved data source
 */
export const cacheOrganizationDataSource = (organization: string, value: OrganizationDataSource): void => {
	try {
		let known: CachedDataSource = {};

		try {
			const cached = localStorage.getItem(STORAGE_KEY_ORG_DATA_SOURCE);
			const parsed = cached ? JSON.parse(cached) : undefined;
			// Merge into whatever object is already stored. An entry written by an
			// earlier version is absorbed rather than discarded: its leftover keys are
			// never read, since reads look up by organization code alone.
			if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
				known = parsed as CachedDataSource;
			}
		} catch { /* a corrupted entry is replaced rather than merged into */ }

		localStorage.setItem(STORAGE_KEY_ORG_DATA_SOURCE, JSON.stringify({
			...known,
			[organization]: value,
		}));
	} catch { /* ignore quota errors */ }
};

/**
 * Resolves whether an organization is backed by Graphlake or by Abyla/Model.
 *
 * Calls the organization lite API of the platform, which is reachable only with a
 * platform bearer token. Apps authenticating with a same-origin session cookie
 * (abylaweb/Model, dataroom/Keep) have no such token: they must not call this at all,
 * which is why `accessToken` is required rather than optional.
 *
 * Never throws — a failure resolves to undefined so the trait is simply omitted.
 *
 * @param app - Current app description, for environment and stack resolution
 * @param organization - Organization code to resolve
 * @param accessToken - Platform OIDC bearer token
 * @returns The data source, or undefined when it could not be resolved
 */
export const fetchOrganizationDataSource = async (
	app: AppDescription,
	organization: string | undefined,
	accessToken: string | undefined,
): Promise<OrganizationDataSource | undefined> => {
	if (!organization || !accessToken) {
		return undefined;
	}

	try {
		const url = buildUrl(URL_PATTERNS.ORGANIZATION_LITE_API, {
			env: app.env,
			organizationCode: organization,
			stack: app.stack,
			// providerDomain is auto-detected by buildUrl when not provided
		});

		const response = await fetch(url, {
			headers: {
				'Authorization': `Bearer ${accessToken}`,
			},
		});

		if (!response.ok) {
			logger.warn(`[OrganizationDataSource] Lite API call failed: ${response.status} ${response.statusText}`);
			return undefined;
		}

		// undefined means the applications were absent from the payload, i.e. the source
		// is unknown rather than resolved. Never cache or emit that: the trait must be
		// missing, not wrong.
		const value = resolveDataSource((await response.json())?.applications);

		if (!value) {
			logger.warn('[OrganizationDataSource] Lite API answered without applications, data source left unresolved');
			return undefined;
		}

		cacheOrganizationDataSource(organization, value);
		return value;
	} catch (e) {
		logger.error('[OrganizationDataSource] Failed to resolve organization data source:', e as Error);
		return undefined;
	}
};

/**
 * Organizations whose resolution is already in flight, so that `setApp` firing on
 * every navigation cannot start a second request for the same one.
 */
const inFlight = new Set<string>();

/**
 * Resolves and caches the data source of an organization, unless it is already known.
 *
 * Called on every navigation, so it is deliberately cheap in the common case: a value
 * cached for this organization, a session without a bearer token, or a resolution
 * already in flight all return without touching the network.
 *
 * The token gate is what keeps apps authenticating by session cookie silent —
 * abylaweb/Model and dataroom/Keep have no platform token, so they emit no request.
 *
 * Never throws.
 *
 * @param app - Current app description, for environment and stack resolution
 * @param organization - Organization code currently being viewed
 * @returns The data source, or undefined when it is not resolvable here and now
 */
export const ensureOrganizationDataSource = async (
	app: AppDescription,
	organization: string | undefined,
): Promise<OrganizationDataSource | undefined> => {
	if (!organization || inFlight.has(organization)) {
		return undefined;
	}

	const cached = readCachedOrganizationDataSource(organization);
	if (cached) {
		return cached;
	}

	const accessToken = getOidcAccessToken();
	if (!accessToken) {
		return undefined;
	}

	inFlight.add(organization);
	try {
		return await fetchOrganizationDataSource(app, organization, accessToken);
	} finally {
		inFlight.delete(organization);
	}
};
